import React, { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { useProductsAnalytics } from 'b2b-apps/core/analytics/context/products/ProductsAnalyticsContext';
import { analyzeProductDetailsPageView } from 'b2b-apps/core/analytics/redux/products/actionCreators';
import { useRemoteRecordsAnalytics } from 'b2b-apps/shop/analytics/hooks';
import { Page } from 'b2b-apps/shop/app/components/App/Page';
import { withAllFailHandler } from 'b2b-apps/shop/common/hoc/withAllFailHandler';
import { withLoader } from 'b2b-apps/shop/common/hoc/withLoader';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { ProductData } from 'b2b-apps/shop/product/components/ProductData/ProductData';
import { ProductDetails } from 'b2b-apps/shop/product/components/ProductDetails/ProductDetails';
import { ProductGallery } from 'b2b-apps/shop/product/components/ProductGallery';
import { Skeleton } from 'b2b-apps/shop/product/components/Skeleton';
import { Props } from './';
import './ProductPage.scss';

const ProductPage = (props: Props): ReactElement | null => {
  const {
    metaTags,
    isInkFinderLinkDisplayed,
    product: { data: product, isLoaded },
  } = props;
  const params = useParams<{ sku: string }>();
  const sku = params.sku || '';
  const { analyzeProductAddToCart } = useProductsAnalytics();

  const enableAnalytics = useRemoteRecordsAnalytics(
    [props.product],
    () => analyzeProductDetailsPageView(sku),
  );

  useEffect(
    () => {
      enableAnalytics();
    },
    [sku],
  );

  if (!isLoaded || isEmpty(product) || !product) {
    return null;
  }

  const handleAddToCart = (sku: string) => {
    analyzeProductAddToCart(sku);
  };

  const { description, manufacturer } = product;
  const { title } = description;
  const { name, logo } = manufacturer;

  return (
    <Page
      className='Product'
      title={product.description.title}
      metaTags={metaTags}
    >
      <section className='ProductPage-MainSection row'>
        <div className='ProductGallery-Wrapper col-md-6'>
          <ProductGallery
            manufacturerLogo={logo}
            manufacturerName={name}
            sku={sku}
            productName={title}
          />
        </div>

        <div className='ProductData-Wrapper col-md-6'>
          <ProductData
            product={product}
            onAddToCart={handleAddToCart}
          />
        </div>
      </section>

      <section className='ProductContent'>
        <ProductDetails
          sku={sku}
          description={product.description}
          isInkFinderLinkDisplayed={isInkFinderLinkDisplayed}
        />
      </section>
    </Page>
  );
};

const ComposedProductPage = compose(
  withLoader((props: Props) => [props.product], true, Skeleton),
  withAllFailHandler((props) => [props.product]),
)(ProductPage);

export { ComposedProductPage as ProductPage };
